import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/fmt";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextIn } from "@/components/forms/Field";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";

const show = (v) => (v === null || v === undefined || v === "" ? "—" : Array.isArray(v) ? v.join(", ") : String(v).slice(0, 80));

export function LeadHistory({ leadId, refreshKey, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/crm/leads/${leadId}/history`).then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, [leadId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const revert = async (e, field) => {
    if (!window.confirm(field ? `Undo the change to "${field.replace("custom.", "")}"?` : "Undo all changes in this edit?")) return;
    setBusy(e.id + (field || ""));
    try {
      await api.post(`/crm/leads/${leadId}/history/${e.id}/revert`, field ? { fields: [field] } : {});
      toast.success("Change undone");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not undo"); }
    finally { setBusy(null); }
  };
  if (!rows) return <div className="text-xs text-neutral-500">Loading…</div>;
  if (!rows.length) return <div className="text-xs text-neutral-600">No changes recorded yet.</div>;
  return (
    <ol className="space-y-3" data-testid="lead-history">
      {rows.map((e, i) => (
        <li key={e.id || i} className="text-xs" data-testid={`history-row-${i}`}>
          <div className="flex items-center gap-2 text-neutral-500"><span className="text-neutral-300">{e.actor}</span><span>{e.action === "update" ? "changed" : e.action}</span>
            {e.reverted && <span className="italic">· undone by {e.reverted}</span>}
            <span className="ml-auto">{fmtDateTime(e.at)}</span>
            {e.action === "update" && e.changes.length > 1 && <button className="px-1.5 py-0.5 rounded border border-[var(--border-strong)] hover:text-[var(--text)]" disabled={!!busy} onClick={() => revert(e)} data-testid={`history-undo-all-${i}`}>Undo all</button>}
          </div>
          {e.changes.map((c) => (
            <div key={c.field} className="group grid grid-cols-[120px_1fr_auto] items-center gap-2 mt-1">
              <span className="text-neutral-500 truncate">{c.field.replace("custom.", "")}</span>
              <span className="truncate"><span className="text-neutral-500 line-through">{show(c.from)}</span> → <span className="text-neutral-200">{show(c.to)}</span></span>
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[var(--border-strong)] text-[var(--text-dim)] hover:text-[var(--text)] disabled:opacity-40" disabled={!!busy}
                onClick={() => revert(e, c.field)} title={`Set back to ${show(c.from)}`} data-testid={`history-undo-${i}-${c.field}`}><Undo2 size={11} />Undo</button>
            </div>
          ))}
        </li>
      ))}
    </ol>
  );
}

const toLocal = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 86400000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export function AddTaskDialog({ lead, users, task, logDone, onClose, onDone }) {
  const edit = !!task;
  const [t, setT] = useState({ label: task?.label || "", due: logDone ? toLocal(new Date().toISOString()) : toLocal(task?.due), owner: (edit ? task.owner : lead.owner) || "", note: task?.note || "" });
  const [fu, setFu] = useState(null);
  const save = async () => {
    if (!t.label.trim()) { toast.error("What needs doing?"); return; }
    const body = { label: t.label.trim(), due: t.due ? new Date(t.due).toISOString() : null, owner: t.owner, note: t.note };
    try {
      if (edit) await api.put(`/crm/tasks/${task._id}`, body);
      else await api.post("/crm/tasks", { lead_id: lead._id, ...body, owner: t.owner || undefined, due: body.due || undefined, status: logDone ? "done" : undefined });
      if (fu?.label?.trim()) await api.post("/crm/tasks", { lead_id: lead._id, label: fu.label.trim(), due: new Date(fu.due).toISOString(), owner: t.owner || undefined });
      toast.success(edit ? "Task updated" : logDone ? "Logged" : "Task added"); onDone();
    } catch (e) { if (e.response?.data?.approval_required) { onClose(); return; } toast.error(e.response?.data?.detail || "Couldn't save task"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${task.label}"?${task.sop ? " This is an SOP step: the SOP won't move on until you add or complete another task." : ""}`)) return;
    try { await api.delete(`/crm/tasks/${task._id}`); toast.success("Task deleted"); onDone(); }
    catch (e) { if (e.response?.data?.approval_required) { onClose(); return; } toast.error(e.response?.data?.detail || "Couldn't delete task"); }
  };
  const reopen = async () => {
    try { await api.put(`/crm/tasks/${task._id}`, { status: "open" }); toast.success("Task reopened"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't reopen"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border-strong)] max-w-sm" data-testid="add-task-dialog">
        <DialogHeader><DialogTitle className="text-base font-medium">{edit ? "Edit task" : logDone ? "Log a done task" : "Add task"} · {lead?.name || task?.lead_name || ""}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={logDone ? "What did you do?" : "Task"}><TextIn value={t.label} onChange={(v) => setT({ ...t, label: v })} testid="add-task-label" autoFocus placeholder={logDone ? "e.g. Sent proposal on WhatsApp" : "e.g. Send proposal deck"} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={logDone ? "Done at" : "Due"}><TextIn type="date" value={(t.due || "").slice(0, 10)} onChange={(v) => setT({ ...t, due: v ? `${v}T10:00` : "" })} testid="add-task-due" /></Field>
            <Field label="Owner">
              <select className="w-full px-2.5 py-1.5 rounded border text-sm" value={t.owner} onChange={(e) => setT({ ...t, owner: e.target.value })} data-testid="add-task-owner">
                <option value="">Unassigned</option>{users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Note"><TextIn value={t.note} onChange={(v) => setT({ ...t, note: v })} testid="add-task-note" placeholder="Optional" /></Field>
          {logDone && (fu ? (
            <div className="grid grid-cols-2 gap-2" data-testid="log-followup">
              <Field label="Follow-up"><TextIn value={fu.label} onChange={(v) => setFu({ ...fu, label: v })} testid="log-followup-label" placeholder="e.g. Chase reply" /></Field>
              <Field label="Due"><TextIn type="date" value={(fu.due || "").slice(0, 10)} onChange={(v) => setFu({ ...fu, due: v ? `${v}T10:00` : "" })} testid="log-followup-due" /></Field>
            </div>
          ) : <button className="text-xs text-[var(--ai)] hover:underline" onClick={() => setFu({ label: "", due: toLocal(null) })} data-testid="log-add-followup">+ Schedule a follow-up</button>)}
          <button className="btn btn-primary w-full justify-center" onClick={save} data-testid="add-task-save">{edit ? "Save changes" : logDone ? "Log it" : "Add task"}</button>
          {edit && (
            <div className="flex gap-2">
              {task.status === "done" && <button className="btn text-xs flex-1 justify-center" onClick={reopen} data-testid="task-reopen">Reopen</button>}
              <button className="btn text-xs flex-1 justify-center hover:text-[var(--breach)]" onClick={del} data-testid="task-delete">Delete task</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
